/**
 * Public Concierge Digital — shareable AI destination guide for OTA partners.
 *
 * No login required. Reuses the existing /api/destination-chat endpoint with a
 * server-side preset persona (see src/lib/concierge-presets.ts).
 */

import { useEffect, useRef, useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Loader2, Send, Sparkles, ExternalLink } from "lucide-react";

import { getConciergePreset } from "@/lib/concierge-presets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/concierge/$preset")({
  loader: ({ params }) => {
    const preset = getConciergePreset(params.preset);
    if (!preset) throw notFound();
    return { preset };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Concierge no disponible" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.preset;
    const title = `${p.title} · Mostrando México`;
    return {
      meta: [
        { title },
        { name: "description", content: p.intro.slice(0, 155) },
        { property: "og:title", content: title },
        { property: "og:description", content: p.intro.slice(0, 155) },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex,follow" },
      ],
    };
  },
  component: ConciergePage,
});

type Msg = { role: "user" | "assistant"; content: string };

function ConciergePage() {
  const { preset } = Route.useLoaderData();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  async function send(text: string) {
    const clean = text.trim();
    if (!clean || loading) return;
    const next: Msg[] = [...messages, { role: "user", content: clean }];
    setMessages(next);
    setInput("");
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/destination-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          destination: preset.destination,
          title: preset.tourTitle,
          tourSlug: preset.tourSlug,
          lang: preset.lang,
          preset: preset.id,
          messages: next.slice(-16).map((m) => ({ role: m.role, content: m.content.slice(0, 2000) })),
        }),
      });
      const data = await res.json().catch(() => ({}) as any);
      if (!res.ok || !data?.message) {
        setError(preset.errorText);
        return;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: String(data.message) }]);
    } catch {
      setError(preset.errorText);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-4 py-6 sm:py-10">
        <header className="mb-6">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            {preset.kicker}
          </p>
          <p className="mt-4 text-sm font-medium text-muted-foreground">{preset.brand}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {preset.title}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
            {preset.intro}
          </p>
        </header>

        {messages.length === 0 && (
          <div className="mb-6 grid gap-2 sm:grid-cols-2">
            {preset.suggestions.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => void send(s.prompt)}
                className="flex items-center gap-2 rounded-xl border border-border bg-card/70 px-3 py-3 text-left text-sm font-medium text-foreground/90 shadow-sm transition hover:border-primary/40 hover:bg-primary/5"
              >
                <span aria-hidden>{s.emoji}</span>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        )}

        <section className="flex-1 space-y-4" aria-live="polite">
          {messages.map((m, i) => (
            <div
              key={i}
              className={
                m.role === "user"
                  ? "ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-3 text-sm text-primary-foreground"
                  : "mr-auto max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-border bg-card px-4 py-3 text-sm leading-relaxed text-foreground"
              }
            >
              {m.content}
            </div>
          ))}
          {loading && (
            <div className="mr-auto inline-flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              …
            </div>
          )}
          {error && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div ref={endRef} />
        </section>

        <form
          className="sticky bottom-0 mt-6 flex items-center gap-2 border-t border-border bg-background/95 py-3 backdrop-blur"
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={preset.inputPlaceholder}
            maxLength={2000}
            className="flex-1"
          />
          <Button type="submit" disabled={loading || !input.trim()}>
            <Send className="mr-1.5 h-4 w-4" />
            {preset.sendLabel}
          </Button>
        </form>

        <footer className="pb-6 pt-2 text-xs text-muted-foreground">
          <a
            href={preset.tourUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            {preset.tourTitle}
            <ExternalLink className="h-3 w-3" />
          </a>
          <p className="mt-2">{preset.disclaimer}</p>
        </footer>
      </div>
    </main>
  );
}
